import {
  ProjectDesignerRoleTags,
  infrastructureType,
  infrastructureLevel,
  infrastructureRelationship,
  InfrastructureType,
  InfrastructureLevel,
  InfrastructureRelationship,
  InfrastructureRequirement,
  normalizeInfrastructureRequirements,
} from '@nohara-dxdev/mt-common';
import { UiSchema } from '@rjsf/utils';
import { JSONSchema7 } from 'json-schema';
import Papa from 'papaparse';

import {
  CsvResultTransformer,
  CsvUtilParsers,
  CsvUtilValidators,
  ColumnDefinition,
  CsvParseResult,
} from '@/features/csv-upload/utils/csv-result-transformer';
import { SettingIdValue } from '@/types/api';

import { CreateItemTemplateDto } from '../api';

import {
  mapInfrastructureType,
  mapInfrastructureLevel,
  mapInfrastructureRelationship,
} from './infrastructure-mappings';
import { mapRoleNameToTag } from './project-role-tags';

export interface CsvAttribute {
  name: string;
  dataType: 'Text' | 'Number' | 'Dropdown';
  valueEnum?: string[];
  unit?: string;
  required: boolean;
}

export interface CsvItemTemplate {
  name: string;
  templateKey: string;
  constructionTypes: string[];
  projectRoles: string[];
  isBoardItem: boolean;
  canConnectToBoard: boolean;
  itemType: string;
  infrastructureRequirements?: InfrastructureRequirement[];
  attributes: CsvAttribute[];
}

// NEW: Updated template type to match new backend structure
export interface ParsedItemTemplate {
  name: string;
  templateKey: string;
  itemType: string;
  constructionTypes: string[];
  projectRoleTags: string[];
  isBoardItem: boolean;
  canConnectToBoard: boolean;
  jsonSchema: JSONSchema7;
  uiSchema: UiSchema;
}

// Role resolution types
export interface RoleConflict {
  csvRoleName: string;
  suggestion?: string;
  availableTags: ProjectDesignerRoleTags[]; // Available role tags for selection
}

export interface RoleTagResolutionResult {
  resolvedTags: string[];
  conflicts: RoleConflict[];
}

// Global item definition reference
export interface GlobalItemDefinition {
  id: string;
  title: string;
  type: string;
  enum?: string[];
}

// Options for the CSV parser
export interface ItemTemplateCsvParserOptions {
  constructionTypes?: SettingIdValue<string>[];
  itemTypes?: SettingIdValue<string>[];
  quotationTypes?: SettingIdValue<string>[];
  locationTypes?: SettingIdValue<string>[];
  organizationRoles?: { id: string; name: string }[]; // Application roles for matching
}

// Role resolution helper with improved matching for role tags
// Supports Japanese names, English names, and enum values directly
export const resolveProjectRoleTags = (csvRoleNames: string[]): RoleTagResolutionResult => {
  const resolvedTags: string[] = [];
  const conflicts: RoleConflict[] = [];

  for (const csvRoleName of csvRoleNames) {
    const trimmedRoleName = csvRoleName.trim();

    // Skip empty role names
    if (!trimmedRoleName) continue;

    // Try to map the role name (Japanese, English, or enum value) to tag
    const mappedTag = mapRoleNameToTag(trimmedRoleName);

    if (mappedTag) {
      // Found a match, add the tag
      if (!resolvedTags.includes(mappedTag)) {
        resolvedTags.push(mappedTag);
      }
    } else {
      // No match found, create a conflict with suggestion and all available tags
      const availableRoleNames = Object.values(ProjectDesignerRoleTags);
      const suggestion = findClosestMatch(trimmedRoleName, availableRoleNames);

      conflicts.push({
        csvRoleName: trimmedRoleName,
        suggestion,
        availableTags: availableRoleNames, // Provide all available role tags
      });
    }
  }

  return { resolvedTags, conflicts };
};

// Helper function to find closest match for suggestions
const findClosestMatch = (input: string, options: string[]): string | undefined => {
  const normalizedInput = input.toLowerCase();

  // Try partial matching first
  const partialMatches = options.filter(
    (option) =>
      option.toLowerCase().includes(normalizedInput) ||
      normalizedInput.includes(option.toLowerCase()),
  );

  if (partialMatches.length > 0) {
    return partialMatches[0];
  }

  // If no partial matches, return the first option as a fallback
  return options[0];
};

// Custom parser for attributes (handles complex attribute format)
// Supports both Japanese (、｜) and English (,|) separators
const parseAttribute = (value: string, required: boolean): CsvAttribute | null => {
  if (!value || value.trim() === '') return null;

  // Find the FIRST comma (half-width , or full-width 、) to separate name from the rest
  const firstCommaMatch = /[,、]/.exec(value);
  if (firstCommaMatch?.index === undefined) return null;

  const firstCommaIndex = firstCommaMatch.index;
  const name = value.substring(0, firstCommaIndex).trim();
  const rest = value.substring(firstCommaIndex + 1).trim();

  if (!name || !rest) return null;

  let dataType: 'Text' | 'Number' | 'Dropdown' = 'Text';
  let valueEnum: string[] | undefined;
  let unit: string | undefined;

  // Check for Dropdown type (case insensitive)
  const dropdownMatch = /^dropdown:\s*(.+)/i.exec(rest);
  if (dropdownMatch) {
    dataType = 'Dropdown';
    let optionsString = dropdownMatch[1].trim();

    // Check if there's a unit at the end
    // The unit would be after the last comma, and should NOT contain pipes
    // This handles cases like: "Dropdown:Opt1|Opt2|Opt3, Unit"
    const lastCommaMatch = /[,、](?=[^,、]*$)/.exec(optionsString); // Find last comma
    if (lastCommaMatch?.index !== undefined) {
      const lastCommaIndex = lastCommaMatch.index;
      const potentialUnit = optionsString.substring(lastCommaIndex + 1).trim();

      // If the potential unit doesn't contain pipes, it's likely a unit
      if (!/[|｜]/.exec(potentialUnit)) {
        unit = potentialUnit;
        optionsString = optionsString.substring(0, lastCommaIndex).trim();
      }
    }

    // Split by pipe separator (support both half-width | and full-width ｜)
    valueEnum = optionsString
      .split(/[|｜]/)
      .map((option) => option.trim())
      .filter((option) => option !== '');
  } else if (rest.toLowerCase().includes('number')) {
    dataType = 'Number';
    // Check if there's a unit after the dataType (split by comma)
    const parts = rest.split(/[,、]/).map((p) => p.trim());
    unit = parts[1] || undefined;
  } else {
    dataType = 'Text';
    // Check if there's a unit after the dataType (split by comma)
    const parts = rest.split(/[,、]/).map((p) => p.trim());
    unit = parts[1] || undefined;
  }

  return {
    name,
    dataType,
    valueEnum,
    unit,
    required,
  };
};

// Custom parser for multiple attributes (columns 8+)
const parseAttributes = (row: any[], startIndex = 8): CsvAttribute[] => {
  const attributes: CsvAttribute[] = [];

  for (let i = startIndex; i < row.length; i++) {
    const attributeString = row[i]?.toString().trim();
    if (attributeString) {
      // make all attributes optional
      const attribute = parseAttribute(attributeString, false);
      if (attribute) {
        attributes.push(attribute);
      }
    }
  }

  return attributes;
};

// Parser for infrastructure requirements
// Format: "electricity|main|parent" or "電気|主|親" or "Electricity|Main|Parent"
// Supports both half-width (|,) and full-width (｜、) separators
// Supports both Japanese and English text for all fields
const parseInfrastructureRequirement = (value: string): InfrastructureRequirement | null => {
  if (!value || value.trim() === '') return null;

  // Split by pipe (| or ｜)
  const parts = value.split(/[|｜]/).map((p) => p.trim());

  if (parts.length < 2) {
    return null; // Need at least infrastructureType and relationshipLevel
  }

  const [infraTypeStr, relLevelStr, relRelationshipStr] = parts;

  // Map infrastructure type (supports Japanese/English)
  const mappedInfraType =
    mapInfrastructureType(infraTypeStr) ?? (infraTypeStr as InfrastructureType);

  // Map relationship level (supports Japanese/English)
  const mappedRelLevel =
    mapInfrastructureLevel(relLevelStr) ?? (relLevelStr as InfrastructureLevel);

  // Map infrastructure relationship (optional, supports Japanese/English)
  let relationship: InfrastructureRelationship | 'both' | undefined;
  if (relRelationshipStr) {
    const mappedRelationship =
      mapInfrastructureRelationship(relRelationshipStr) ??
      (relRelationshipStr as InfrastructureRelationship);
    relationship = mappedRelationship;
  }

  return {
    infrastructureType: mappedInfraType,
    relationshipLevel: mappedRelLevel,
    infrastructureRelationship: relationship,
  };
};

// Parser for multiple infrastructure requirements
// Supports comma-separated (both , and 、) list of requirements
const parseInfrastructureRequirements = (value: string): InfrastructureRequirement[] => {
  if (!value || value.trim() === '') return [];

  // Split by comma (support both half-width , and full-width 、)
  const requirementStrings = value.split(/[,、]/).map((s) => s.trim());

  const requirements: InfrastructureRequirement[] = [];
  for (const reqString of requirementStrings) {
    if (reqString) {
      const requirement = parseInfrastructureRequirement(reqString);
      if (requirement) {
        requirements.push(requirement);
      }
    }
  }

  // Normalize: remove duplicates and combine parent + child into "both"
  return normalizeInfrastructureRequirements(requirements);
};

export interface ItemTemplateCsvParseResult extends CsvParseResult<CreateItemTemplateDto> {
  roleConflicts: TemplateRoleConflict[];
}

// NEW: Template-specific role conflict type
export interface TemplateRoleConflict {
  templateIndex: number;
  templateName: string;
  conflicts: RoleConflict[];
}

const csvTemplateToItemTemplate = (
  csvTemplate: CsvItemTemplate,
  _options?: ItemTemplateCsvParserOptions,
): { template: CreateItemTemplateDto; conflicts: RoleConflict[] } => {
  // Create minimal JSON schema for attributes only
  const attributeProperties: JSONSchema7 = {
    type: 'object',
    title: 'アイテム属性',
    $id: 'ItemAttributes',
    properties: {},
    additionalProperties: false,
    required: [],
  };
  const uiSchemaProperties: Record<string, any> = {};

  // Add attributes section if there are any
  if (csvTemplate.attributes.length > 0) {
    csvTemplate.attributes.forEach((attribute, index) => {
      const propertyKey = generatePropertyKey(attribute.name);
      const attributeSchema = csvAttributeToJsonSchemaProperty(attribute, index);
      (attributeProperties.properties as Record<string, unknown>)[propertyKey] = attributeSchema;
      if (attribute.required) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        attributeProperties.required!.push(propertyKey);
      }
    });
  }

  // Normalize infrastructure requirements if they exist
  const infrastructureRequirements = csvTemplate.infrastructureRequirements
    ? normalizeInfrastructureRequirements(csvTemplate.infrastructureRequirements)
    : [];

  // Create the template with the new structure
  const template: CreateItemTemplateDto = {
    name: csvTemplate.name,
    templateKey: csvTemplate.templateKey,
    description: '',
    itemTypeId: csvTemplate.itemType,
    isBoardItem: csvTemplate.isBoardItem,
    canConnectToBoard: csvTemplate.canConnectToBoard,
    constructionTypeIds: csvTemplate.constructionTypes,
    projectRoleTags: [], // Will be resolved from role names to tags
    jsonSchema: attributeProperties,
    uiSchema: uiSchemaProperties,
    infrastructureRequirements,
  };

  let conflicts: RoleConflict[] = [];

  // Convert role names to role tags
  const resolutionResult = resolveProjectRoleTags(csvTemplate.projectRoles);
  template.projectRoleTags = resolutionResult.resolvedTags as ProjectDesignerRoleTags[];
  conflicts = resolutionResult.conflicts;

  return { template, conflicts };
};

// Helper functions
const getValueType = (dataType: 'Text' | 'Number' | 'Dropdown'): 'string' | 'number' => {
  switch (dataType) {
    case 'Number':
      return 'number';
    default:
      return 'string';
  }
};

const generatePropertyKey = (attributeName: string): string => {
  if (!attributeName || attributeName.trim() === '') {
    return `attribute_${Date.now()}`;
  }

  let key = attributeName
    .trim()
    .replace(/[\s\-()[\]/\\]+/g, '_')
    .replace(/[^\w\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF\u0030-\u0039]/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_+|_+$/g, '');

  if (!key) {
    key = `attribute_${Date.now()}`;
  }

  if (/^[0-9]/.test(key)) {
    key = `attr_${key}`;
  }

  return key;
};

const csvAttributeToJsonSchemaProperty = (
  attribute: CsvAttribute,
  order: number,
): JSONSchema7 & { 'x-unit'?: string; 'x-order'?: number } => {
  const valueSchema: {
    type: 'string' | 'number';
    title: 'Value';
    enum?: string[];
  } = {
    type: getValueType(attribute.dataType),
    title: 'Value',
  };

  if (attribute.dataType === 'Dropdown' && attribute.valueEnum) {
    valueSchema.enum = attribute.valueEnum;
  }

  return {
    ...valueSchema,
    title: attribute.name,
    'x-unit': attribute.unit,
    'x-order': order,
  };
};

export const createItemTemplateCsvParser = (
  constructionTypes: SettingIdValue<string>[],
  itemTypes: SettingIdValue<string>[],
  t?: (key: string, options?: any) => string,
) => {
  const columns: ColumnDefinition<any, any[]>[] = [
    {
      key: 0,
      field: 'name',
      required: true,
      parser: CsvUtilParsers.string(),
      validator: CsvUtilValidators.required(),
    },
    {
      key: 1,
      field: 'templateKey',
      required: true,
      parser: CsvUtilParsers.string(),
      validator: CsvUtilValidators.required(),
    },
    {
      key: 2,
      field: 'constructionTypes',
      required: true,
      parser: CsvUtilParsers.array(','),
      transformer: (value: string[]) => {
        return value.map(
          (type) => (constructionTypes.find((t) => t.value === type) as { id: string }).id,
        );
      },
      validator: (value: string[]) => {
        if (value.length === 0) {
          return t
            ? t('csvImport.errors.atLeastOneConstructionType')
            : 'At least one construction type is required';
        }

        // Validate each construction type against allowed options
        const invalidTypes = value.filter(
          (type) => !constructionTypes.some((t) => t.value === type),
        );
        if (invalidTypes.length > 0) {
          const allowed = constructionTypes.map((t) => t.value).join(', ');
          return t
            ? t('csvImport.errors.constructionTypeInvalid', {
                value: invalidTypes.join(', '),
                allowed,
              })
            : `Invalid construction type(s): ${invalidTypes.join(', ')}. Allowed values: ${allowed}`;
        }

        return null;
      },
    },
    {
      key: 3,
      field: 'projectRoles',
      required: false,
      parser: CsvUtilParsers.array(','),
      defaultValue: [],
    },
    {
      key: 4,
      field: 'isBoardItem',
      required: true,
      parser: CsvUtilParsers.boolean(['yes', 'true', '1'], ['no', 'false', '0']),
    },
    {
      key: 5,
      field: 'canConnectToBoard',
      required: true,
      parser: CsvUtilParsers.boolean(['yes', 'true', '1'], ['no', 'false', '0']),
    },
    {
      key: 6,
      field: 'itemType',
      required: true,
      parser: CsvUtilParsers.string(),
      transformer: (value: string) => {
        return (itemTypes.find((t) => t.value === value) as { id: string }).id;
      },
      validator: (value: string) => {
        if (!value || value.trim() === '') {
          return t ? t('csvImport.errors.itemTypeRequired') : 'Item type is required';
        }

        // Validate item type against allowed options
        if (!itemTypes.some((t) => t.value === value)) {
          const allowed = itemTypes.map((t) => t.value).join(', ');
          return t
            ? t('csvImport.errors.itemTypeInvalid', { value, allowed })
            : `Invalid item type: ${value}. Allowed values: ${allowed}`;
        }

        return null;
      },
    },
    {
      key: 7,
      field: 'infrastructureRequirements',
      required: false,
      parser: (value: string) => {
        if (!value || value.trim() === '') return [];
        return parseInfrastructureRequirements(value);
      },
      validator: (value: InfrastructureRequirement[]) => {
        if (!value.length) return null;

        // Validate each parsed requirement
        const validInfraTypes = Object.values(infrastructureType);
        const validLevels = Object.values(infrastructureLevel);
        const validRelationships = [...Object.values(infrastructureRelationship), 'both'];

        for (const req of value) {
          if (!validInfraTypes.includes(req.infrastructureType)) {
            const allowed = validInfraTypes.join(', ');
            return t
              ? t('csvImport.errors.infrastructureTypeInvalid', {
                  value: req.infrastructureType,
                  allowed,
                })
              : `Invalid infrastructure type: "${req.infrastructureType}". Allowed values: ${allowed}`;
          }
          if (!validLevels.includes(req.relationshipLevel)) {
            const allowed = validLevels.join(', ');
            return t
              ? t('csvImport.errors.relationshipLevelInvalid', {
                  value: req.relationshipLevel,
                  allowed,
                })
              : `Invalid relationship level: "${req.relationshipLevel}". Allowed values: ${allowed}`;
          }
          if (
            req.infrastructureRelationship &&
            !validRelationships.includes(req.infrastructureRelationship)
          ) {
            const allowed = validRelationships.join(', ');
            return t
              ? t('csvImport.errors.infrastructureRelationshipInvalid', {
                  value: req.infrastructureRelationship,
                  allowed,
                })
              : `Invalid infrastructure relationship: "${req.infrastructureRelationship}". Allowed values: ${allowed}`;
          }
        }

        return null;
      },
    },
  ];

  return new CsvResultTransformer<CsvItemTemplate>({
    columns,
    hasHeader: true,
    skipEmptyRows: true,
    maxErrors: 100,
    translate: t,
    rowTransformer: (data: Partial<CsvItemTemplate>, row: string[]) => {
      // Parse attributes from remaining columns (8+)
      const attributes = parseAttributes(row, 8);

      return {
        ...data,
        attributes,
      } as CsvItemTemplate;
    },
  });
};

// Convenience function to parse item template CSV and convert to JSON Schema
export const parseItemTemplateCsvResult = async (
  csvParseResult: Papa.ParseResult<string[]>,
  options?: ItemTemplateCsvParserOptions,
  t?: (key: string, options?: any) => string,
): Promise<ItemTemplateCsvParseResult> => {
  const itemTemplateCsvParser = createItemTemplateCsvParser(
    options?.constructionTypes ?? [],
    options?.itemTypes ?? [],
    t,
  );
  const csvResult = await itemTemplateCsvParser.parse(csvParseResult);

  // Convert CSV templates to new structure and collect role conflicts
  const convertedData: CreateItemTemplateDto[] = [];
  const roleConflicts: TemplateRoleConflict[] = [];

  csvResult.data.forEach((csvTemplate, index) => {
    const { template, conflicts } = csvTemplateToItemTemplate(csvTemplate, options);

    convertedData.push(template);

    if (conflicts.length > 0) {
      roleConflicts.push({
        templateIndex: index,
        templateName: template.name,
        conflicts,
      });
    }
  });

  return {
    ...csvResult,
    data: convertedData,
    roleConflicts,
  };
};
